// ContentSelectionConfigForm — visual editor for the JSONB shape that lives
// at lab_email_templates.content_selection_config. Used in two places:
//
//   1. LabContentSelector — preview tool (form + live POST /api/lab/content/preview)
//   2. LabTemplateEditor  — form is embedded as the "Content Selection" panel
//
// All Pydantic field semantics on the backend (count 1-20, suppression
// 0-365, etc.) are mirrored here in the input bounds so the editor never
// produces a config the backend would reject.

import { useMemo, useState } from 'react'
import { Panel } from '../components/Panel'

const PUB_NAMES: Record<string, string> = {
  rc_car_action: 'RC Car Action',
  flight_journal: 'Flight Journal',
  model_airplane_news: 'Model Airplane News',
  diecast: 'Die Cast X',
}

const STORY_TYPES = [
  'firsthand_account',
  'iconic_aircraft_vehicle',
  'historical_deep_dive',
  'combat_story',
  'profile',
  'technical_analysis',
  'how_to',
  'review',
  'product_announcement',
  'interview',
  'editorial',
  'news',
  'other',
]

export type ContentSelectionConfig = {
  count: number
  publication_slug: string | null
  story_types: string[]
  eras: string[]
  tags_include: string[]
  tags_exclude: string[]
  member_only: boolean | null
  require_enriched: boolean
  recency_after: string | null
  suppression_window_days: number
  randomize: boolean
}

export const DEFAULT_CONTENT_SELECTION_CONFIG: ContentSelectionConfig = {
  count: 4,
  publication_slug: null,
  story_types: [],
  eras: [],
  tags_include: [],
  tags_exclude: [],
  member_only: null,
  require_enriched: true,
  recency_after: null,
  suppression_window_days: 90,
  randomize: false,
}

export function ContentSelectionConfigForm({
  config, onChange, label = 'Selection Config', accent = 'amber',
}: {
  config: ContentSelectionConfig
  onChange: (c: ContentSelectionConfig) => void
  label?: string
  accent?: 'amber' | 'cyan' | 'violet' | 'red' | 'green' | 'none'
}) {
  const set = <K extends keyof ContentSelectionConfig>(k: K, v: ContentSelectionConfig[K]) =>
    onChange({ ...config, [k]: v })

  return (
    <Panel label={label} accent={accent}>
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label="Article count"
            value={config.count}
            min={1}
            max={20}
            onChange={(n) => set('count', n)}
          />
          <SelectField
            label="Publication"
            value={config.publication_slug ?? ''}
            onChange={(v) => set('publication_slug', v || null)}
            options={[
              { value: '', label: 'All publications' },
              ...Object.entries(PUB_NAMES).map(([value, label]) => ({ value, label })),
            ]}
          />
        </div>

        <ChipMultiSelect
          label="Story types"
          options={STORY_TYPES}
          selected={config.story_types}
          onChange={(v) => set('story_types', v)}
        />

        <TagsInput
          label="Eras"
          placeholder="WWII, Cold War, Vietnam War, modern"
          values={config.eras}
          onChange={(v) => set('eras', v)}
        />

        <div className="grid grid-cols-2 gap-4">
          <TagsInput
            label="Tags include (any match)"
            placeholder="pacific-theater, d-day"
            values={config.tags_include}
            onChange={(v) => set('tags_include', v)}
          />
          <TagsInput
            label="Tags exclude (none match)"
            placeholder="restoration"
            values={config.tags_exclude}
            onChange={(v) => set('tags_exclude', v)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <SelectField
            label="Member-only filter"
            value={
              config.member_only === true ? 'true'
              : config.member_only === false ? 'false' : ''
            }
            onChange={(v) =>
              set('member_only', v === 'true' ? true : v === 'false' ? false : null)
            }
            options={[
              { value: '', label: 'Either' },
              { value: 'true', label: 'Member-only only' },
              { value: 'false', label: 'Public only' },
            ]}
          />
          <DateInput
            label="Posted on or after"
            value={config.recency_after ?? ''}
            onChange={(v) => set('recency_after', v || null)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label="Suppression window (days)"
            value={config.suppression_window_days}
            min={0}
            max={365}
            onChange={(n) => set('suppression_window_days', n)}
          />
          <div className="flex flex-col gap-3 pt-5">
            <CheckboxRow
              checked={config.require_enriched}
              onChange={(c) => set('require_enriched', c)}
              label="Require AI enrichment"
              hint="Skip articles that haven't been classified yet"
            />
            <CheckboxRow
              checked={config.randomize}
              onChange={(c) => set('randomize', c)}
              label="Randomize order"
              hint="Default: engagement_score DESC, post_date DESC"
            />
          </div>
        </div>
      </div>
    </Panel>
  )
}

// ---------- Reusable inputs ----------

export function NumberInput({
  label, value, min, max, onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  onChange: (n: number) => void
}) {
  return (
    <label className="block">
      <span className="label mb-1.5 block">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const n = Number(e.target.value)
          if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, n)))
        }}
        className="w-full bg-ink-700 border border-ink-500 px-3 py-1.5 font-mono text-[13px] text-paper"
      />
    </label>
  )
}

export function SelectField({
  label, value, onChange, options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: Array<{ value: string; label: string }>
}) {
  return (
    <label className="block">
      <span className="label mb-1.5 block">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-ink-700 border border-ink-500 px-3 py-1.5 font-mono text-[12px] text-paper uppercase tracking-label cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  )
}

export function DateInput({
  label, value, onChange,
}: { label: string; value: string; onChange: (v: string) => void }) {
  const dateOnly = value ? value.slice(0, 10) : ''
  return (
    <label className="block">
      <span className="label mb-1.5 block">{label}</span>
      <input
        type="date"
        value={dateOnly}
        onChange={(e) => onChange(e.target.value ? `${e.target.value}T00:00:00Z` : '')}
        className="w-full bg-ink-700 border border-ink-500 px-3 py-1.5 font-mono text-[13px] text-paper"
      />
    </label>
  )
}

export function CheckboxRow({
  checked, onChange, label, hint,
}: {
  checked: boolean
  onChange: (c: boolean) => void
  label: string
  hint?: string
}) {
  return (
    <label className="flex items-start gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1"
      />
      <span>
        <span className="label block">{label}</span>
        {hint && <span className="font-mono text-[11px] text-paper-trace">{hint}</span>}
      </span>
    </label>
  )
}

export function ChipMultiSelect({
  label, options, selected, onChange,
}: {
  label: string
  options: string[]
  selected: string[]
  onChange: (v: string[]) => void
}) {
  const toggle = (v: string) => {
    onChange(selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v])
  }
  return (
    <div>
      <span className="label mb-1.5 block">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const on = selected.includes(opt)
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              className={`
                font-mono text-[10px] uppercase tracking-label px-2 py-1 border
                ${on
                  ? 'border-amber-glow text-amber-glow bg-amber-wash'
                  : 'border-ink-500 text-paper-trace hover:border-paper-trace hover:text-paper'}
              `}
            >
              {opt}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function TagsInput({
  label, placeholder, values, onChange,
}: {
  label: string
  placeholder: string
  values: string[]
  onChange: (v: string[]) => void
}) {
  const [draft, setDraft] = useState('')

  const commit = () => {
    const fresh = draft
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
    if (fresh.length === 0) return
    const merged = [...values]
    for (const f of fresh) if (!merged.includes(f)) merged.push(f)
    onChange(merged)
    setDraft('')
  }

  const remove = (v: string) => onChange(values.filter((x) => x !== v))

  const display = useMemo(() => values, [values])

  return (
    <div>
      <span className="label mb-1.5 block">{label}</span>
      <div className="flex flex-wrap gap-1.5 mb-1.5 min-h-[26px]">
        {display.map((v) => (
          <span
            key={v}
            className="font-mono text-[11px] text-paper border border-paper-trace px-1.5 py-0.5 inline-flex items-center gap-1.5"
          >
            {v}
            <button
              onClick={() => remove(v)}
              className="text-radar-red hover:text-paper"
              title="Remove"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            commit()
          }
        }}
        onBlur={commit}
        placeholder={placeholder}
        className="w-full bg-ink-700 border border-ink-500 px-3 py-1.5 font-mono text-[12px] text-paper"
      />
    </div>
  )
}
